import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Building, Plus, Trash2, Edit, Search,
  GraduationCap, MapPin, Trophy, Users, BookOpen,
  Loader2, CheckCircle2, AlertCircle, X, Save
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UNIVERSITY_TYPES = [
  { value: 'russell_group', label: 'Russell Group' },
  { value: 'campus', label: 'Campus University' },
  { value: 'city', label: 'City University' },
  { value: 'ancient', label: 'Ancient University' },
  { value: 'other', label: 'Other' }
];

const UK_REGIONS = [
  'England', 'Scotland', 'Wales', 'Northern Ireland'
];

const PathwaysAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [universities, setUniversities] = useState({ builtin: [], custom: [] });
  const [statistics, setStatistics] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const [selectedUni, setSelectedUni] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [uniForm, setUniForm] = useState({
    id: '',
    name: '',
    type: 'other',
    location: '',
    ranking_uk: 100,
    ranking_world: 1000,
    acceptance_rate: 50,
    student_satisfaction: 80,
    graduate_prospects: 75,
    oxbridge: false,
    features: [],
    popular_courses: [],
    application_info: {
      deadline: 'January 29',
      ucas_code: '',
      interview_required: 'No',
      admissions_tests: [],
      personal_statement_tips: ''
    },
    courses: {}
  });

  const [courseForm, setCourseForm] = useState({
    course_id: '',
    name: '',
    ucas_code: '',
    duration: '3 years',
    typical_offer: 'AAA',
    required_subjects: [],
    admissions_test: '',
    interview: false,
    places_per_year: 100,
    applications_per_place: 5
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [uniRes, statsRes] = await Promise.all([
        axios.get(`${API}/pathways/admin/universities`, { headers }),
        axios.get(`${API}/pathways/statistics`, { headers })
      ]);
      setUniversities(uniRes.data);
      setStatistics(statsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin or Tutor access required');
        navigate('/categories');
      } else {
        toast.error('Failed to load data');
      }
    }
    setLoading(false);
  };

  const resetUniForm = () => {
    setUniForm({
      id: '',
      name: '',
      type: 'other',
      location: '',
      ranking_uk: 100,
      ranking_world: 1000,
      acceptance_rate: 50,
      student_satisfaction: 80,
      graduate_prospects: 75,
      oxbridge: false,
      features: [],
      popular_courses: [],
      application_info: {
        deadline: 'January 29',
        ucas_code: '',
        interview_required: 'No',
        admissions_tests: [],
        personal_statement_tips: ''
      },
      courses: {}
    });
  };

  const handleAddUniversity = async () => {
    if (!uniForm.name || !uniForm.id || !uniForm.location) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/pathways/admin/universities`, uniForm, { headers });
      toast.success('University added successfully!');
      setShowAddDialog(false);
      resetUniForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add university');
    }
    setSaving(false);
  };

  const handleEditUniversity = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/pathways/admin/universities/${selectedUni.id}`, uniForm, { headers });
      toast.success('University updated successfully!');
      setShowEditDialog(false);
      setSelectedUni(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update university');
    }
    setSaving(false);
  };

  const handleDeleteUniversity = async (uniId) => {
    if (!confirm('Are you sure you want to delete this university?')) return;
    
    try {
      await axios.delete(`${API}/pathways/admin/universities/${uniId}`, { headers });
      toast.success('University deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleAddCourse = async () => {
    if (!courseForm.course_id || !courseForm.name) {
      toast.error('Please fill in course ID and name');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/pathways/admin/universities/${selectedUni.id}/courses?course_id=${courseForm.course_id}`,
        courseForm,
        { headers }
      );
      toast.success('Course added!');
      setShowCourseDialog(false);
      setCourseForm({
        course_id: '',
        name: '',
        ucas_code: '',
        duration: '3 years',
        typical_offer: 'AAA',
        required_subjects: [],
        admissions_test: '',
        interview: false,
        places_per_year: 100,
        applications_per_place: 5
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add course');
    }
    setSaving(false);
  };

  const openEditDialog = (uni) => {
    setSelectedUni(uni);
    setUniForm({
      ...uni,
      features: uni.features || [],
      popular_courses: uni.popular_courses || [],
      application_info: uni.application_info || {
        deadline: 'January 29',
        ucas_code: '',
        interview_required: 'No',
        admissions_tests: [],
        personal_statement_tips: ''
      },
      courses: uni.courses || {}
    });
    setShowEditDialog(true);
  };

  const filteredUniversities = () => {
    let unis = activeTab === 'custom' ? universities.custom : 
               activeTab === 'builtin' ? universities.builtin :
               [...universities.builtin, ...universities.custom];
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      unis = unis.filter(u => 
        u.name.toLowerCase().includes(q) || 
        u.location?.toLowerCase().includes(q)
      );
    }
    
    return unis.sort((a, b) => (a.ranking_uk || 999) - (b.ranking_uk || 999));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/pathways')}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to UniGuide
          </Button>
          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
            Admin Panel
          </Badge>
        </div>

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            University Management
          </h1>
          <p className="text-slate-400">Add, edit, and manage universities for Eds UniGuide™</p>
        </div>

        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 text-center">
                <Building className="h-8 w-8 text-indigo-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{statistics.total_universities}</p>
                <p className="text-xs text-slate-400">Total Universities</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 text-center">
                <Trophy className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{statistics.by_type?.russell_group || 0}</p>
                <p className="text-xs text-slate-400">Russell Group</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 text-center">
                <GraduationCap className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{statistics.oxbridge_count}</p>
                <p className="text-xs text-slate-400">Oxbridge</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 text-center">
                <Plus className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{statistics.custom_count}</p>
                <p className="text-xs text-slate-400">Custom Added</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search & Actions */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search universities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <Button
            onClick={() => { resetUniForm(); setShowAddDialog(true); }}
            className="bg-gradient-to-r from-indigo-600 to-purple-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add University
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-slate-800/50">
            <TabsTrigger value="all">All ({universities.builtin.length + universities.custom.length})</TabsTrigger>
            <TabsTrigger value="builtin">Built-in ({universities.builtin.length})</TabsTrigger>
            <TabsTrigger value="custom">Custom ({universities.custom.length})</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* University List */}
        <div className="grid gap-4">
          {filteredUniversities().map((uni) => (
            <Card key={uni.id} className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-white">{uni.name}</h3>
                      {uni.oxbridge && <Badge className="bg-amber-500/20 text-amber-400 text-xs">Oxbridge</Badge>}
                      {uni.is_custom && <Badge className="bg-green-500/20 text-green-400 text-xs">Custom</Badge>}
                      <Badge className="bg-slate-600 text-slate-300 text-xs">{uni.type}</Badge>
                    </div>
                    <p className="text-sm text-slate-400 flex items-center gap-1 mb-2">
                      <MapPin className="h-3 w-3" /> {uni.location}
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                      <span>UK Rank: #{uni.ranking_uk}</span>
                      <span>•</span>
                      <span>Acceptance: {uni.acceptance_rate}%</span>
                      <span>•</span>
                      <span>Courses: {Object.keys(uni.courses || {}).length}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {uni.is_custom && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(uni)}
                          className="border-slate-600 text-slate-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteUniversity(uni.id)}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add University Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Add New University</DialogTitle>
            </DialogHeader>
            <UniversityForm 
              form={uniForm} 
              setForm={setUniForm}
              onSave={handleAddUniversity}
              saving={saving}
              isEdit={false}
            />
          </DialogContent>
        </Dialog>

        {/* Edit University Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Edit University</DialogTitle>
            </DialogHeader>
            <UniversityForm 
              form={uniForm} 
              setForm={setUniForm}
              onSave={handleEditUniversity}
              saving={saving}
              isEdit={true}
              onAddCourse={() => setShowCourseDialog(true)}
            />
          </DialogContent>
        </Dialog>

        {/* Add Course Dialog */}
        <Dialog open={showCourseDialog} onOpenChange={setShowCourseDialog}>
          <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Add Course</DialogTitle>
            </DialogHeader>
            <CourseForm 
              form={courseForm}
              setForm={setCourseForm}
              onSave={handleAddCourse}
              saving={saving}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// University Form Component
const UniversityForm = ({ form, setForm, onSave, saving, isEdit, onAddCourse }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">University ID *</Label>
          <Input
            value={form.id}
            onChange={(e) => setForm({...form, id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
            className="bg-slate-800 border-slate-700 text-white"
            placeholder="e.g., durham"
            disabled={isEdit}
          />
        </div>
        <div>
          <Label className="text-slate-300">Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className="bg-slate-800 border-slate-700 text-white"
            placeholder="e.g., Durham University"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {UNIVERSITY_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">Location *</Label>
          <Input
            value={form.location}
            onChange={(e) => setForm({...form, location: e.target.value})}
            className="bg-slate-800 border-slate-700 text-white"
            placeholder="e.g., Durham, England"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-slate-300">UK Ranking</Label>
          <Input
            type="number"
            value={form.ranking_uk}
            onChange={(e) => setForm({...form, ranking_uk: parseInt(e.target.value) || 100})}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-slate-300">World Ranking</Label>
          <Input
            type="number"
            value={form.ranking_world}
            onChange={(e) => setForm({...form, ranking_world: parseInt(e.target.value) || 1000})}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-slate-300">Acceptance Rate %</Label>
          <Input
            type="number"
            value={form.acceptance_rate}
            onChange={(e) => setForm({...form, acceptance_rate: parseFloat(e.target.value) || 50})}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Student Satisfaction %</Label>
          <Input
            type="number"
            value={form.student_satisfaction}
            onChange={(e) => setForm({...form, student_satisfaction: parseInt(e.target.value) || 80})}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-slate-300">Graduate Prospects %</Label>
          <Input
            type="number"
            value={form.graduate_prospects}
            onChange={(e) => setForm({...form, graduate_prospects: parseInt(e.target.value) || 75})}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <div>
        <Label className="text-slate-300">Features (comma separated)</Label>
        <Input
          value={form.features?.join(', ') || ''}
          onChange={(e) => setForm({...form, features: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
          className="bg-slate-800 border-slate-700 text-white"
          placeholder="e.g., Campus university, Strong research"
        />
      </div>

      <div>
        <Label className="text-slate-300">Popular Courses (comma separated)</Label>
        <Input
          value={form.popular_courses?.join(', ') || ''}
          onChange={(e) => setForm({...form, popular_courses: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
          className="bg-slate-800 border-slate-700 text-white"
          placeholder="e.g., Law, Medicine, Engineering"
        />
      </div>

      <div className="border-t border-slate-700 pt-4">
        <h4 className="text-white font-medium mb-3">Application Info</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">UCAS Code</Label>
            <Input
              value={form.application_info?.ucas_code || ''}
              onChange={(e) => setForm({
                ...form,
                application_info: {...form.application_info, ucas_code: e.target.value}
              })}
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="e.g., D86"
            />
          </div>
          <div>
            <Label className="text-slate-300">Deadline</Label>
            <Input
              value={form.application_info?.deadline || 'January 29'}
              onChange={(e) => setForm({
                ...form,
                application_info: {...form.application_info, deadline: e.target.value}
              })}
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-slate-300">Personal Statement Tips</Label>
          <Textarea
            value={form.application_info?.personal_statement_tips || ''}
            onChange={(e) => setForm({
              ...form,
              application_info: {...form.application_info, personal_statement_tips: e.target.value}
            })}
            className="bg-slate-800 border-slate-700 text-white"
            rows={2}
          />
        </div>
      </div>

      {/* Courses Section (Edit mode only) */}
      {isEdit && (
        <div className="border-t border-slate-700 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-medium">Courses ({Object.keys(form.courses || {}).length})</h4>
            <Button size="sm" onClick={onAddCourse}>
              <Plus className="h-4 w-4 mr-1" /> Add Course
            </Button>
          </div>
          <div className="space-y-2">
            {Object.entries(form.courses || {}).map(([id, course]) => (
              <div key={id} className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                <div>
                  <p className="text-white text-sm">{course.name}</p>
                  <p className="text-xs text-slate-400">Offer: {course.typical_offer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DialogFooter>
        <Button onClick={onSave} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {isEdit ? 'Update University' : 'Add University'}
        </Button>
      </DialogFooter>
    </div>
  );
};

// Course Form Component
const CourseForm = ({ form, setForm, onSave, saving }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Course ID *</Label>
          <Input
            value={form.course_id}
            onChange={(e) => setForm({...form, course_id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
            className="bg-slate-800 border-slate-700 text-white"
            placeholder="e.g., computer_science"
          />
        </div>
        <div>
          <Label className="text-slate-300">UCAS Code</Label>
          <Input
            value={form.ucas_code}
            onChange={(e) => setForm({...form, ucas_code: e.target.value})}
            className="bg-slate-800 border-slate-700 text-white"
            placeholder="e.g., G400"
          />
        </div>
      </div>

      <div>
        <Label className="text-slate-300">Course Name *</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({...form, name: e.target.value})}
          className="bg-slate-800 border-slate-700 text-white"
          placeholder="e.g., Computer Science"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Duration</Label>
          <Input
            value={form.duration}
            onChange={(e) => setForm({...form, duration: e.target.value})}
            className="bg-slate-800 border-slate-700 text-white"
            placeholder="e.g., 3 years"
          />
        </div>
        <div>
          <Label className="text-slate-300">Typical Offer</Label>
          <Input
            value={form.typical_offer}
            onChange={(e) => setForm({...form, typical_offer: e.target.value})}
            className="bg-slate-800 border-slate-700 text-white"
            placeholder="e.g., AAA or A*AA"
          />
        </div>
      </div>

      <div>
        <Label className="text-slate-300">Required Subjects (comma separated)</Label>
        <Input
          value={form.required_subjects?.join(', ') || ''}
          onChange={(e) => setForm({...form, required_subjects: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
          className="bg-slate-800 border-slate-700 text-white"
          placeholder="e.g., Maths, Physics"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Places per Year</Label>
          <Input
            type="number"
            value={form.places_per_year}
            onChange={(e) => setForm({...form, places_per_year: parseInt(e.target.value) || 100})}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-slate-300">Applications per Place</Label>
          <Input
            type="number"
            value={form.applications_per_place}
            onChange={(e) => setForm({...form, applications_per_place: parseInt(e.target.value) || 5})}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <DialogFooter>
        <Button onClick={onSave} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
          Add Course
        </Button>
      </DialogFooter>
    </div>
  );
};

export default PathwaysAdmin;
